// Fundação de tokens visuais da modernização do InfoChat (Entrega M2).
//
// Função pura — sem JSX, sem I/O, sem leitura de localStorage/backend, sem
// nenhuma dependência nova. Não é consumida por nenhuma tela/componente
// nesta entrega; existe apenas para ficar disponível em `theme.infoChat`
// para as entregas visuais futuras (M3+), conforme aprovado.
//
// Direção visual aprovada: "Opção 2 — tecnológica, organizada e
// corporativa". Os valores de referência abaixo (`LIGHT_REFERENCE_TOKENS`
// / `DARK_REFERENCE_TOKENS`) são exatamente os aprovados na especificação;
// os poucos valores não especificados diretamente (ex.: `surfaceRaised` no
// claro, `surfaceSubtle` no escuro, tons de borda "forte" e texto
// desabilitado) são DERIVADOS deterministicamente dos valores aprovados via
// `mixHex`, nunca escolhidos arbitrariamente — a justificativa técnica de
// cada derivação está comentada no ponto de uso.

const clampByte = value => Math.min(255, Math.max(0, Math.round(value)));

// Exportado (ao contrário de `rgbToHex`/`mixHex` abaixo, que continuam
// privados deste módulo) para reuso em `infoChatLoginStyles.js`, que
// precisa compor cores translúcidas como `rgba(...)` — formato aceito de
// forma universal por qualquer parser CSS, ao contrário de hex de 8
// dígitos (`#RRGGBBAA`), que alguns ambientes (incluindo o parser CSS do
// jsdom usado nos testes) rejeitam silenciosamente.
export const hexToRgb = hex => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map(c => c + c)
          .join("")
      : normalized;
  const intValue = parseInt(expanded.slice(0, 6), 16);
  return {
    r: (intValue >> 16) & 255,
    g: (intValue >> 8) & 255,
    b: intValue & 255
  };
};

const rgbToHex = ({ r, g, b }) => {
  const toHex = channel => clampByte(channel).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

const isValidHexColor = value =>
  typeof value === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());

// Interpola de `fromHex` em direção a `towardHex` por `amount` (0 a 1).
// Usada só para DERIVAR tokens auxiliares a partir das cores de referência
// já aprovadas — nunca para recalcular `palette.primary`/`contrastText`
// (essas continuam exclusivamente sob responsabilidade do App.js atual).
const mixHex = (fromHex, towardHex, amount) => {
  const from = hexToRgb(fromHex);
  const toward = hexToRgb(towardHex);
  const clampedAmount = Math.min(1, Math.max(0, amount));

  return rgbToHex({
    r: from.r + (toward.r - from.r) * clampedAmount,
    g: from.g + (toward.g - from.g) * clampedAmount,
    b: from.b + (toward.b - from.b) * clampedAmount
  });
};

const DEFAULT_PRIMARY_COLOR = "#065183"; // mesmo fallback já usado hoje em App.js:27

// Valores de referência aprovados, literais (nunca derivados).
const LIGHT_REFERENCE_TOKENS = {
  canvas: "#F4F7FB",
  surface: "#FFFFFF",
  surfaceSubtle: "#F8FAFC",
  borderSubtle: "#DCE3EC",
  textPrimary: "#14213D",
  textSecondary: "#64748B"
};

const DARK_REFERENCE_TOKENS = {
  canvas: "#0F1724",
  surface: "#151F2E",
  surfaceRaised: "#1A2638",
  borderSubtle: "#2A3A50",
  textPrimary: "#F4F7FB",
  textSecondary: "#A9B6C8"
};

// Escala de raio compartilhada entre os dois modos — `md` reflete o mesmo
// valor já usado em `shape.borderRadius` (App.js), documentado aqui para
// reuso futuro sem duplicar a definição em si.
const RADII = Object.freeze({
  none: 0,
  sm: 4,
  md: 8,
  lg: 16,
  pill: 999
});

// Mesma curva de easing já usada nos overrides de MuiButton/MuiTab em
// App.js ('cubic-bezier(0.4, 0, 0.2, 1)') — reaproveitada, não reinventada.
const TRANSITIONS = Object.freeze({
  duration: Object.freeze({ fast: 150, base: 300, slow: 450 }),
  easing: Object.freeze({ standard: "cubic-bezier(0.4, 0, 0.2, 1)" })
});

// Referência de espaçamento/densidade — mesma unidade padrão do MUI
// (theme.spacing usa 8px por padrão); não altera `theme.spacing` em si,
// só documenta a escala para uso consistente nas entregas futuras.
const SPACING_REFERENCE = Object.freeze({
  unit: 8,
  scale: Object.freeze({ xs: 4, sm: 8, md: 16, lg: 24, xl: 32 }),
  density: Object.freeze({ compact: 4, comfortable: 8, spacious: 12 })
});

// Dimensões de layout já reais no app hoje (drawer largo/recolhido) —
// documentadas para reuso, não redefinidas: drawerWidth espelha
// `layout/index.js` (`drawerWidth = 240`); drawerWidthCollapsed espelha
// `theme.spacing(7)`/`theme.spacing(9)` já usados em `drawerPaperClose`
// (56px abaixo de `sm`, 72px em `sm` e acima); headerHeight espelha a
// altura padrão do MUI para `Toolbar variant="dense"`.
const LAYOUT_REFERENCE = Object.freeze({
  drawerWidth: 240,
  drawerWidthCollapsed: Object.freeze({ base: 56, up: 72 }),
  headerHeight: 48
});

// Cores semânticas de status — mesmo vocabulário já usado em
// ConnectionHealthChip/ConnectionHealthStatus (healthy/connecting/
// awaitingScan/alert/unmonitored), para que as duas versões nunca
// divirjam. `unmonitored` reaproveita diretamente o textSecondary do modo,
// em vez de inventar mais um cinza.
const STATUS_MAIN_COLORS = Object.freeze({
  healthy: "#16A34A",
  connecting: "#2563EB",
  awaitingScan: "#D97706",
  alert: "#DC2626"
});

const buildStatusTokens = ({ mode, canvas, surfaceRaised, textSecondary }) => {
  // Tom de fundo suave (para chips/badges futuros) derivado da cor
  // principal de cada status misturada com a superfície do modo — claro
  // mistura com o canvas (fundo muito claro), escuro mistura com a
  // superfície elevada (fundo escuro sutilmente tingido).
  const tintTarget = mode === "dark" ? surfaceRaised : canvas;
  const tintAmount = mode === "dark" ? 0.78 : 0.88;

  const buildEntry = main => ({
    main,
    surface: mixHex(main, tintTarget, tintAmount)
  });

  return {
    healthy: buildEntry(STATUS_MAIN_COLORS.healthy),
    connecting: buildEntry(STATUS_MAIN_COLORS.connecting),
    awaitingScan: buildEntry(STATUS_MAIN_COLORS.awaitingScan),
    alert: buildEntry(STATUS_MAIN_COLORS.alert),
    unmonitored: buildEntry(textSecondary)
  };
};

/**
 * Tokens visuais semânticos do InfoChat, puros e sem efeitos colaterais.
 * Não é aplicado a nenhum componente nesta entrega — só fica disponível em
 * `theme.infoChat` para consumo nas entregas visuais futuras.
 *
 * @param {"light"|"dark"} mode - modo de cor atual. Qualquer valor que não
 *   seja exatamente "dark" cai em "light" (fallback seguro).
 * @param {string} primaryColor - cor institucional dinâmica já resolvida
 *   pelo App.js (primaryColorLight ou primaryColorDark, conforme o modo).
 *   É preservada tal como recebida em `brand.primaryColor`; esta função
 *   nunca recalcula `palette.primary`/`contrastText`.
 */
export const createInfoChatDesignTokens = (mode, primaryColor) => {
  const safeMode = mode === "dark" ? "dark" : "light";
  const safePrimaryColor = isValidHexColor(primaryColor) ? primaryColor : DEFAULT_PRIMARY_COLOR;

  const ref = safeMode === "dark" ? DARK_REFERENCE_TOKENS : LIGHT_REFERENCE_TOKENS;

  const surfaces =
    safeMode === "dark"
      ? {
          canvas: ref.canvas,
          base: ref.surface,
          raised: ref.surfaceRaised,
          // Não há valor de referência aprovado para "subtle" no escuro —
          // derivado como ponto intermediário entre canvas e surface,
          // ficando propositalmente muito próximo da base (é o papel de
          // uma superfície "subtle": quase imperceptível).
          subtle: mixHex(ref.canvas, ref.surface, 0.55)
        }
      : {
          canvas: ref.canvas,
          base: ref.surface,
          subtle: ref.surfaceSubtle,
          // Não há valor de referência aprovado para "raised" no claro —
          // no tema claro a elevação é comunicada por sombra, não por
          // mudança de cor (convenção já usada nos overrides de MuiPaper
          // em App.js), então "raised" reaproveita a mesma cor de "base".
          raised: ref.surface
        };

  const borders = {
    subtle: ref.borderSubtle,
    // "Strong" derivado misturando a borda sutil com o texto primário do
    // mesmo modo — nunca um hex escolhido à mão.
    strong: mixHex(ref.borderSubtle, ref.textPrimary, 0.35)
  };

  const text = {
    primary: ref.textPrimary,
    secondary: ref.textSecondary,
    // Desabilitado derivado misturando o texto secundário em direção à
    // superfície base do modo — mais próximo do fundo, como esperado para
    // um estado "disabled".
    disabled: mixHex(ref.textSecondary, ref.surface, 0.45)
  };

  return {
    mode: safeMode,
    brand: {
      primaryColor: safePrimaryColor
    },
    surfaces,
    borders,
    text,
    radii: { ...RADII },
    shadows:
      safeMode === "dark"
        ? {
            sm: "0 1px 2px rgba(0, 0, 0, 0.35)",
            md: "0 6px 16px rgba(0, 0, 0, 0.45)",
            lg: "0 14px 32px rgba(0, 0, 0, 0.55)"
          }
        : {
            sm: "0 1px 2px rgba(15, 23, 36, 0.08)",
            md: "0 4px 12px rgba(15, 23, 36, 0.10)",
            lg: "0 10px 24px rgba(15, 23, 36, 0.14)"
          },
    transitions: {
      duration: { ...TRANSITIONS.duration },
      easing: { ...TRANSITIONS.easing }
    },
    status: buildStatusTokens({
      mode: safeMode,
      canvas: surfaces.canvas,
      surfaceRaised: surfaces.raised,
      textSecondary: text.secondary
    }),
    layout: {
      drawerWidth: LAYOUT_REFERENCE.drawerWidth,
      drawerWidthCollapsed: { ...LAYOUT_REFERENCE.drawerWidthCollapsed },
      headerHeight: LAYOUT_REFERENCE.headerHeight
    },
    spacing: {
      unit: SPACING_REFERENCE.unit,
      scale: { ...SPACING_REFERENCE.scale },
      density: { ...SPACING_REFERENCE.density }
    }
  };
};

export default createInfoChatDesignTokens;
